/**
 * bootAdvance — the BOOT screen's timer-vs-input race as pure logic.
 *
 * Exactly one advance ever happens: either the armed timer fires or an input
 * skips first; whichever wins cancels the other. Kept DOM-free so the race is
 * unit-testable with fake timers (node vitest environment).
 */

export interface BootAdvance {
  /** Schedule the auto-advance. Re-arming while armed/advanced is a no-op. */
  arm(delayMs: number): void;
  /** Input skip. Returns true if THIS call caused the advance. */
  skip(): boolean;
  /** Cancel everything; the advance can no longer happen. */
  dispose(): void;
  /** True once onAdvance has run. */
  readonly done: boolean;
}

export function createBootAdvance(onAdvance: () => void): BootAdvance {
  let advanced = false;
  let disposed = false;
  let timer: ReturnType<typeof setTimeout> | null = null;

  const clear = (): void => {
    if (timer !== null) {
      clearTimeout(timer);
      timer = null;
    }
  };

  const fire = (): boolean => {
    if (advanced || disposed) return false;
    advanced = true;
    clear();
    onAdvance();
    return true;
  };

  return {
    arm(delayMs: number): void {
      if (advanced || disposed || timer !== null) return;
      timer = setTimeout(fire, delayMs);
    },
    skip(): boolean {
      return fire();
    },
    dispose(): void {
      disposed = true;
      clear();
    },
    get done(): boolean {
      return advanced;
    },
  };
}
